import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Bell, MessageCircle, Menu, Sparkles, ChevronDown, Newspaper, User, LayoutGrid, FileText, Settings, UserCircle, ShieldCheck, Lock, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';
import { employees, appLinks, documents, knowledgeArticles, notifications as seedNotifications, Article } from './data';
import { useApp } from './store';
interface TopBarProps {
  onMenuClick: () => void;
}
interface SearchResult {
  type: 'News' | 'Person' | 'App' | 'Document';
  label: string;
  detail: string;
  to: string;
  icon: any;
}
function buildIndex(query: string, articles: Article[]): SearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const results: SearchResult[] = [];
  articles.forEach(a => {
    if (a.title.toLowerCase().includes(q) || a.category.toLowerCase().includes(q)) results.push({
      type: 'News',
      label: a.title,
      detail: a.category,
      to: `/news/${a.id}`,
      icon: Newspaper
    });
  });
  employees.forEach(e => {
    if (e.name.toLowerCase().includes(q) || e.title.toLowerCase().includes(q)) results.push({
      type: 'Person',
      label: e.name,
      detail: e.title,
      to: `/people/${e.id}`,
      icon: User
    });
  });
  appLinks.forEach(a => {
    if (a.label.toLowerCase().includes(q)) results.push({
      type: 'App',
      label: a.label,
      detail: a.description || 'App',
      to: '/apps',
      icon: LayoutGrid
    });
  });
  [...documents, ...knowledgeArticles].forEach(d => {
    if (d.title.toLowerCase().includes(q)) results.push({
      type: 'Document',
      label: d.title,
      detail: d.category,
      to: knowledgeArticles.includes(d) ? '/knowledge' : '/documents',
      icon: FileText
    });
  });
  return results.slice(0, 8);
}
export function TopBar({
  onMenuClick
}: TopBarProps) {
  const navigate = useNavigate();
  const {
    user,
    isAdmin,
    lockAdmin,
    clearIdentity,
    articles
  } = useApp();
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifs, setNotifs] = useState(seedNotifications);
  const searchRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => buildIndex(query, articles), [query, articles]);
  const unreadCount = notifs.filter(n => n.unread).length;
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const goToResult = (to: string) => {
    setSearchOpen(false);
    setQuery('');
    navigate(to);
  };
  const markAllRead = () => setNotifs(n => n.map(x => ({
    ...x,
    unread: false
  })));
  return <header className="sticky top-0 z-30 bg-white border-b border-navy-100" data-id="element-818">
      <div className="h-16 px-4 sm:px-6 flex items-center gap-4" data-id="element-819">
        <button className="lg:hidden p-2 text-navy-600 hover:bg-navy-50 rounded-lg" onClick={onMenuClick} aria-label="Open navigation" data-id="element-820">
          <Menu className="w-6 h-6" data-id="element-821" />
        </button>

        {/* Smart search */}
        <div className="flex-1 max-w-xl relative" ref={searchRef} data-id="element-822">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-navy-400 pointer-events-none" data-id="element-823">
            <Search className="w-5 h-5" data-id="element-824" />
          </div>
          <input type="text" value={query} onChange={e => {
          setQuery(e.target.value);
          setSearchOpen(true);
        }} onFocus={() => setSearchOpen(true)} placeholder="Search people, news, files, apps…" className="w-full pl-11 pr-24 py-2.5 rounded-full bg-navy-50 border border-transparent focus:border-gold-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 text-sm text-navy-900 placeholder:text-navy-400 transition-all" data-id="element-825" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[0.65rem] font-semibold text-gold-600 bg-gold-50 px-2 py-1 rounded-full uppercase tracking-wide pointer-events-none" data-id="element-826">
            <Sparkles className="w-3 h-3" data-id="element-827" />
            AI
          </span>

          <AnimatePresence data-id="element-828">
            {searchOpen && query.trim() && <motion.div initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 6
          }} transition={{
            duration: 0.15
          }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-navy-100 shadow-xl overflow-hidden z-50" data-id="element-829">
                {results.length === 0 ? <div className="p-6 text-center text-sm text-navy-400" data-id="element-830">
                    No results for “{query}”.
                  </div> : <ul className="max-h-96 overflow-y-auto py-2" data-id="element-831">
                    {results.map((r, i) => {
                const Icon = r.icon;
                return <li key={i} data-id="element-832">
                          <button onClick={() => goToResult(r.to)} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-navy-50 text-left transition-colors" data-id="element-833">
                            <span className="w-9 h-9 shrink-0 rounded-lg bg-navy-50 flex items-center justify-center text-navy-500" data-id="element-834">
                              <Icon className="w-4 h-4" data-id="element-835" />
                            </span>
                            <span className="min-w-0 flex-1" data-id="element-836">
                              <span className="block text-sm font-semibold text-navy-900 truncate" data-id="element-837">
                                {r.label}
                              </span>
                              <span className="block text-xs text-navy-400 truncate" data-id="element-838">
                                {r.detail}
                              </span>
                            </span>
                            <span className="text-[0.6rem] font-bold uppercase tracking-wide text-navy-400" data-id="element-839">
                              {r.type}
                            </span>
                          </button>
                        </li>;
              })}
                  </ul>}
              </motion.div>}
          </AnimatePresence>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 ml-auto" data-id="element-840">
          <button onClick={() => toast('Direct messages are coming soon.')} className="relative p-2.5 text-navy-600 hover:bg-navy-50 rounded-full" aria-label="Messages" data-id="element-841">
            <MessageCircle className="w-5 h-5" data-id="element-842" />
          </button>

          {/* Notifications */}
          <div className="relative" ref={notifRef} data-id="element-843">
            <button onClick={() => {
            setNotifOpen(v => !v);
            setProfileOpen(false);
          }} className="relative p-2.5 text-navy-600 hover:bg-navy-50 rounded-full" aria-label="Notifications" data-id="element-844">
              <Bell className="w-5 h-5" data-id="element-845" />
              {unreadCount > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-gold-500 rounded-full ring-2 ring-white" data-id="element-846" />}
            </button>

            <AnimatePresence data-id="element-847">
              {notifOpen && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 6
            }} transition={{
              duration: 0.15
            }} className="absolute right-0 mt-2 w-80 bg-white rounded-xl border border-navy-100 shadow-xl overflow-hidden z-50" data-id="element-848">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-navy-50" data-id="element-849">
                    <span className="font-serif font-bold text-navy-900" data-id="element-850">
                      Notifications
                    </span>
                    {unreadCount > 0 && <button onClick={markAllRead} className="inline-flex items-center gap-1 text-xs font-semibold text-gold-600 hover:text-gold-700" data-id="element-851">
                        <Check className="w-3.5 h-3.5" data-id="element-852" /> Mark all read
                      </button>}
                  </div>
                  <ul className="max-h-96 overflow-y-auto" data-id="element-853">
                    {notifs.map(n => <li key={n.id} data-id="element-854">
                        <Link to={n.to} onClick={() => setNotifOpen(false)} className={`flex items-start gap-3 px-4 py-3 hover:bg-navy-50 transition-colors ${n.unread ? 'bg-gold-50/50' : ''}`} data-id="element-855">
                          <img src={n.avatar} alt="" className="w-9 h-9 rounded-full shrink-0" data-id="element-856" />
                          <span className="min-w-0" data-id="element-857">
                            <span className="block text-sm text-navy-800 leading-snug" data-id="element-858">
                              {n.text}
                            </span>
                            <span className="block text-xs text-navy-400 mt-0.5" data-id="element-859">
                              {n.time}
                            </span>
                          </span>
                          {n.unread && <span className="w-2 h-2 rounded-full bg-gold-500 mt-1.5 shrink-0" data-id="element-860" />}
                        </Link>
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Profile */}
          <div className="relative" ref={profileRef} data-id="element-861">
            <button onClick={() => {
            setProfileOpen(v => !v);
            setNotifOpen(false);
          }} className="flex items-center gap-2 pl-1.5 pr-2 sm:pr-3 py-1.5 rounded-full hover:bg-navy-50 transition-colors ml-1" data-id="element-862">
              <img src={user.avatar} alt="Your profile" className="w-8 h-8 rounded-full object-cover ring-2 ring-gold-400" data-id="element-863" />
              <span className="hidden sm:block text-sm font-semibold text-navy-900" data-id="element-864">
                {user.name}
              </span>
              <ChevronDown className={`hidden sm:block w-4 h-4 text-navy-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} data-id="element-865" />
            </button>

            <AnimatePresence data-id="element-866">
              {profileOpen && <motion.div initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 6
            }} transition={{
              duration: 0.15
            }} className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-navy-100 shadow-xl overflow-hidden z-50" data-id="element-867">
                  <div className="px-4 py-3 border-b border-navy-50" data-id="element-868">
                    <p className="text-sm font-bold text-navy-900" data-id="element-869">
                      {user.name}
                    </p>
                    <p className="text-xs text-navy-400" data-id="element-870">
                      {user.title} · {user.location}
                    </p>
                  </div>
                  <div className="py-1" data-id="element-871">
                    <Link to="/people/u3" onClick={() => setProfileOpen(false)} className="flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50" data-id="element-872">
                      <UserCircle className="w-4 h-4 text-navy-400" data-id="element-873" /> My
                      Profile
                    </Link>
                    <Link to="/settings" onClick={() => setProfileOpen(false)} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50" data-id="element-874">
                      <Settings className="w-4 h-4 text-navy-400" data-id="element-875" /> Settings
                    </Link>
                    <button onClick={() => {
                  setProfileOpen(false);
                  clearIdentity();
                  navigate('/');
                }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50 text-left" data-id="element-876">
                      <RefreshCw className="w-4 h-4 text-navy-400" data-id="element-877" /> Switch
                      profile
                    </button>
                    <div className="my-1 border-t border-navy-50" data-id="element-878" />
                    {isAdmin ? <>
                        <Link to="/admin" onClick={() => setProfileOpen(false)} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50" data-id="element-879">
                          <ShieldCheck className="w-4 h-4 text-navy-400" data-id="element-880" /> Site
                          Admin
                        </Link>
                        <button onClick={() => {
                    lockAdmin();
                    setProfileOpen(false);
                    navigate('/');
                  }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50 text-left" data-id="element-881">
                          <Lock className="w-4 h-4 text-navy-400" data-id="element-882" /> Lock admin
                        </button>
                      </> : <Link to="/admin" onClick={() => setProfileOpen(false)} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50" data-id="element-883">
                        <ShieldCheck className="w-4 h-4 text-navy-400" data-id="element-884" /> Unlock
                        Site Admin
                      </Link>}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>;
}